import { IPuzzle, ICell, IClue, CellState, CellType } from 'js/crossword/types';
import { wrapIndex } from 'framework/utils/array-utils';

export function isCurrentCellRevealed(puzzleData: IPuzzle): boolean {
  const cell = getCell(puzzleData, puzzleData.currentCellId);
  return cell.state === CellState.Revealed;
}

export function isCurrentWordRevealed(puzzleData: IPuzzle): boolean {
  const clue = getClue(puzzleData, puzzleData.currentClueId);
  return clue.cells.every((cellId) => {
    return getCell(puzzleData, cellId).state === CellState.Revealed;
  });
}

export function getCluesWithCellIds(puzzleData: IPuzzle, cellId: string): IClue[] {
  return puzzleData.clues.filter((clue) => clue.cells.includes(cellId));
}

export function getCurrentClue(puzzleData: IPuzzle): IClue {
  return getClue(puzzleData, puzzleData.currentClueId);
}

export function getCurrentCell(puzzleData: IPuzzle): ICell {
  return getCell(puzzleData, puzzleData.currentCellId);
}

export function isInBounds(puzzleData: IPuzzle, x: number, y: number): boolean {
  return x >= 0 && x < puzzleData.columns && y >= 0 && y < puzzleData.rows;
}

export function getCellAtPosition(puzzleData: IPuzzle, x: number, y: number): ICell {
  return puzzleData.cells.find((cell) => cell.x === x && cell.y === y)!;
}

export function getCell(puzzleData: IPuzzle, id: string): ICell {
  return puzzleData.cells.find((cell) => cell.id === id)!;
}

export function getClue(puzzleData: IPuzzle, id: string): IClue {
  return puzzleData.clues.find((clue) => clue.id === id)!;
}

export function getOtherDirectionClue(puzzleData: IPuzzle): IClue | null {
  const currentClue = getCurrentClue(puzzleData);
  const currentCell = getCurrentCell(puzzleData);

  return (
    getCluesWithCellIds(puzzleData, currentCell.id).find((clue) => {
      return clue.type !== currentClue.type;
    }) || null
  );
}

export function getNextCellInClue(clue: IClue, currentId: string): string {
  const currentIndex = clue.cells.indexOf(currentId);
  return clue.cells[wrapIndex(clue.cells, currentIndex + 1)];
}

export function isPuzzleSolved(puzzleData: IPuzzle): boolean {
  return puzzleData.cells
    .filter((cell) => cell.type === CellType.Regular)
    .every((cell) => {
      return cell.letter === cell.solution;
    });
}

export function solvePuzzle(puzzleData: IPuzzle): void {
  getRegularCells(puzzleData).forEach((cell) => {
    cell.letter = cell.solution;
  });
}

export function getCorrectFillRate(puzzleData: IPuzzle): number {
  const regularCells = getRegularCells(puzzleData);
  return regularCells.reduce((prev, cur) => prev + (cur.letter === cur.solution ? 1 : 0), 0) / regularCells.length;
}

export function getRegularCells(puzzleData: IPuzzle): ICell[] {
  return puzzleData.cells.filter((cell) => cell.type === CellType.Regular);
}

export function getFillRate(puzzleData: IPuzzle): number {
  const regularCells = getRegularCells(puzzleData);
  return regularCells.reduce((prev, cur) => prev + (cur.letter !== '' ? 1 : 0), 0) / regularCells.length;
}
